import { STTProvider } from './stt-models.constants';

export interface STTLanguageOption {
	value: string;
	label: string;
	supportedModels?: string[]; // Optional: specific models that support this language
}

export const STT_LANGUAGES: Record<STTProvider, STTLanguageOption[]> = {
	[STTProvider.DEEPGRAM]: [
		// Multi-language option (recommended for Nova-3)
		{ value: 'multi', label: 'Multi-language (Auto-detect)' },

		// Major languages with excellent support
		{ value: 'en', label: 'English' },
		{ value: 'en-US', label: 'English (US)' },
		{ value: 'en-GB', label: 'English (UK)' },
		{ value: 'en-AU', label: 'English (Australia)' },
		{ value: 'es', label: 'Spanish' },
		{ value: 'fr', label: 'French' },
		{ value: 'de', label: 'German' },
		{ value: 'it', label: 'Italian' },
		{ value: 'pt', label: 'Portuguese' },
		{ value: 'pt-BR', label: 'Portuguese (Brazil)' },
		{ value: 'nl', label: 'Dutch' },
		{ value: 'ru', label: 'Russian' },
		{ value: 'ja', label: 'Japanese' },
		{ value: 'ko', label: 'Korean' },
		{ value: 'zh', label: 'Chinese (Simplified)' },
		{ value: 'zh-CN', label: 'Chinese (Mainland)' },
		{ value: 'zh-TW', label: 'Chinese (Taiwan)' },
		{ value: 'zh-HK', label: 'Chinese (Hong Kong)' },
		{ value: 'hi', label: 'Hindi' },

		// Additional languages
		{ value: 'bg', label: 'Bulgarian' },
		{ value: 'ca', label: 'Catalan' },
		{ value: 'cs', label: 'Czech' },
		{ value: 'da', label: 'Danish' },
		{ value: 'el', label: 'Greek' },
		{ value: 'et', label: 'Estonian' },
		{ value: 'fi', label: 'Finnish' },
		{ value: 'he', label: 'Hebrew' },
		{ value: 'hr', label: 'Croatian' },
		{ value: 'hu', label: 'Hungarian' },
		{ value: 'id', label: 'Indonesian' },
		{ value: 'lt', label: 'Lithuanian' },
		{ value: 'lv', label: 'Latvian' },
		{ value: 'ms', label: 'Malay' },
		{ value: 'no', label: 'Norwegian' },
		{ value: 'pl', label: 'Polish' },
		{ value: 'ro', label: 'Romanian' },
		{ value: 'sk', label: 'Slovak' },
		{ value: 'sl', label: 'Slovenian' },
		{ value: 'sv', label: 'Swedish' },
		{ value: 'th', label: 'Thai' },
		{ value: 'tr', label: 'Turkish' },
		{ value: 'uk', label: 'Ukrainian' },
		{ value: 'vi', label: 'Vietnamese' },
	],
	[STTProvider.DEEPGRAM_FLUX]: [
		// Flux currently only supports English
		{ value: 'en', label: 'English' },
	],
	[STTProvider.ASSEMBLYAI]: [
		// AssemblyAI currently only supports English for streaming
		{ value: 'en_us', label: 'English (US)' },
	],
	[STTProvider.ELEVENLABS]: [
		// ElevenLabs Scribe v2 supports 90+ languages
		{ value: 'multi', label: 'Multi-language (Auto-detect)' },
		{ value: 'en', label: 'English' },
		{ value: 'es', label: 'Spanish' },
		{ value: 'fr', label: 'French' },
		{ value: 'de', label: 'German' },
		{ value: 'it', label: 'Italian' },
		{ value: 'pt', label: 'Portuguese' },
		{ value: 'nl', label: 'Dutch' },
		{ value: 'pl', label: 'Polish' },
		{ value: 'ru', label: 'Russian' },
		{ value: 'ja', label: 'Japanese' },
		{ value: 'ko', label: 'Korean' },
		{ value: 'zh', label: 'Chinese' },
		{ value: 'ar', label: 'Arabic' },
		{ value: 'hi', label: 'Hindi' },
		{ value: 'tr', label: 'Turkish' },
		{ value: 'sv', label: 'Swedish' },
		{ value: 'da', label: 'Danish' },
		{ value: 'fi', label: 'Finnish' },
		{ value: 'no', label: 'Norwegian' },
	],
};

export const DEFAULT_STT_LANGUAGES: Record<STTProvider, string> = {
	[STTProvider.DEEPGRAM]: 'multi', // Multi-language auto-detect
	[STTProvider.DEEPGRAM_FLUX]: 'en', // English only for now
	[STTProvider.ASSEMBLYAI]: 'en_us', // Only option for streaming
	[STTProvider.ELEVENLABS]: 'multi', // Multi-language auto-detect
};

// Helper to check if a language is supported by a provider
export function isLanguageSupported(
	provider: STTProvider,
	language: string,
): boolean {
	return STT_LANGUAGES[provider].some((lang) => lang.value === language);
}

// Get default language if current selection is not supported
export function getDefaultLanguageForProvider(
	provider: STTProvider,
	currentLanguage?: string,
): string {
	if (currentLanguage && isLanguageSupported(provider, currentLanguage)) {
		return currentLanguage;
	}

	return DEFAULT_STT_LANGUAGES[provider];
}
